<script setup lang="ts">
  import { computed } from 'vue';
  import type { MpfSelectOptionItem, MpfSelectProps, MpfSelectEmits } from './MpfSelect.props';
  import { mpfSelectDefaults } from './MpfSelect.props';
  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(defineProps<MpfSelectProps>(), mpfSelectDefaults);
  const emit = defineEmits<MpfSelectEmits>();

  defineOptions({
    name: 'MpfSelect',
  });

  const selectId = computed(() => props.id || props.name || 'mpf-select');
  const inputName = computed(() => props.name || selectId.value);

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);
  const hintId = computed(() => (props.hint ? `${selectId.value}-hint` : ''));
  const errorId = computed(() => (hasErrors.value ? `${selectId.value}-error` : ''));

  const describedBy = computed(() => {
    const ids = [hintId.value, errorId.value, props.ariaDescribedby].filter(Boolean);
    return ids.length ? ids.join(' ') : undefined;
  });

  const getItemId = (item: MpfSelectOptionItem, index: number) =>
    item.id || `${selectId.value}-${index}`;

  const isSelected = (item: MpfSelectOptionItem) =>
    props.modelValue !== null && String(props.modelValue) === String(item.value);

  const isEmptySelected = computed(() => !props.items.some(isSelected));

  const onChange = (event: Event) => {
    if (props.disabled) return;

    const raw = (event.target as HTMLSelectElement).value;
    const match = props.items.find((item) => String(item.value) === raw);
    const value = match ? match.value : null;

    emit('update:modelValue', value);
    emit('change', value, event);
  };

  const onBlur = (event: FocusEvent) => emit('blur', event);
  const onFocus = (event: FocusEvent) => emit('focus', event);
</script>

<template>
  <div class="mpf-form-group" :class="{ 'mpf-form-group--error': hasErrors }">
    <MpfLabel
      :for-id="selectId"
      :label="label"
      :heading-level-size="headingLevelSize"
      :aria-describedby="hintId || undefined"
    />

    <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

    <span v-if="hasErrors" class="mpf-error-message" :id="errorId">
      <span class="mpf-u-visually-hidden">Error:</span>
      {{ errors![0] }}
    </span>

    <span class="mpf-select-wrapper">
      <select
        class="mpf-select"
        :class="{ 'mpf-select--error': hasErrors }"
        :id="selectId"
        :name="inputName"
        :disabled="disabled"
        :required="required || undefined"
        :aria-describedby="describedBy"
        :aria-label="ariaLabel || undefined"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
        data-private
      >
        <option v-if="includeEmptyOption" value="" :selected="isEmptySelected">
          {{ emptyOptionLabel }}
        </option>

        <option
          v-for="(item, index) in items"
          :key="getItemId(item, index)"
          :value="item.value"
          :disabled="item.disabled"
          :selected="isSelected(item)"
        >
          {{ item.label }}
        </option>
      </select>
    </span>
  </div>
</template>

<style lang="scss" scoped>
  @use './MpfSelect.scss';
</style>
